import {useCallback, useEffect, useRef, useState} from 'react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Button, Dialog, Flash} from '@primer/react'
import {useQueryClient} from '@github-ui/react-query'
import {announce} from '@github-ui/aria-live'
import {useClickAnalytics} from '@github-ui/use-analytics'

import {usePreviewStackData} from '../../../page-data/loaders/use-preview-stack-data'
import type {PreviewStackData, PreviewStackPull} from '../../../page-data/payloads/preview-stack'
import {useHeaderPageDataQueryKey} from '../../../page-data/loaders/use-header-page-data'
import {useCreateStackOrAppendMutation} from '../../../mutations/use-create-stack-or-append-mutation'
import {buildPullRequestUserActionContext} from '../../../analytics/pull-request-user-action'
import type {StackCreatedInfo} from '../StackCreatedBanner'
import {PreviewStackDescription} from './PreviewStackDescription'
import {PreviewStackList} from './PreviewStackList'
import styles from './PreviewStackDialog.module.css'

export type PreviewStackDialogProps = {
  currentPullNumber: number
  onClose: () => void
  onStackCreated: (info: StackCreatedInfo) => void
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
}

export function PreviewStackDialog({
  currentPullNumber,
  onClose,
  onStackCreated,
  returnFocusRef,
}: PreviewStackDialogProps) {
  const {data, isPending, isError} = usePreviewStackData()
  const [originalData, setOriginalData] = useState<PreviewStackData | undefined | null>(null)
  const [pulls, setPulls] = useState<PreviewStackPull[]>([])
  const [submitError, setSubmitError] = useState<string | null>(null)
  const {mutate: createStackOrAppend} = useCreateStackOrAppendMutation()
  const queryClient = useQueryClient()
  // Use custom submission state to prevent resubmission while updating the header page data
  const [isSubmitting, setIsSubmitting] = useState(false)
  const headerPageDataQueryKey = useHeaderPageDataQueryKey()
  const hasAnnouncedDataRef = useRef(false)

  if (data !== originalData) {
    setOriginalData(data)
    setPulls(data ?? [])
  }

  const isModified = originalData !== null && pulls.length !== (originalData?.length ?? 0)
  const hasPulls = pulls.length > 0
  // Failure covers both a fetch error and a successful response with no
  // pulls -- either way there's no preview to show.
  const showFailure = !isPending && (isError || !hasPulls)
  // The tail (bottom) pull carries the existing stack id when this preview
  // walked down into one. Pulls are top-first in display order, so the bottom
  // is the last element.
  const lastPull = pulls[pulls.length - 1]
  const existingStackId = lastPull?.stackId
  const existingStackNumber = lastPull?.stackNumber
  const isExtendingExistingStack = existingStackId != null
  const createStackLabel = isExtendingExistingStack ? 'Add to stack' : 'Create stack'
  const hasOtherNewlyAddedPulls = pulls.some(pull => pull.stackId == null && pull.number !== currentPullNumber)
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const listContainerRef = useRef<HTMLDivElement>(null)
  const pullsCount = pulls.length
  useEffect(() => {
    if (isPending || hasAnnouncedDataRef.current) return
    const element = listContainerRef.current ?? undefined
    if (showFailure) {
      announce('Preview failed to generate. Try refreshing the page.', {element})
    } else {
      announce(`Proposed stack loaded with ${pullsCount} pull ${pullsCount === 1 ? 'request' : 'requests'}.`, {element})
    }
    hasAnnouncedDataRef.current = true
  }, [isPending, showFailure, pullsCount])

  const handleRemoveTop = useCallback(() => {
    setPulls(prev => prev.slice(1))
  }, [])

  const handleReset = () => {
    sendClickAnalyticsEvent(
      buildPullRequestUserActionContext({
        category: 'stack_preview',
        action: 'reset',
      }),
    )
    if (originalData) setPulls(originalData)
  }

  const handleSubmit = () => {
    if (!hasPulls) return
    setSubmitError(null)
    setIsSubmitting(true)
    sendClickAnalyticsEvent(
      buildPullRequestUserActionContext({
        category: 'stack_preview',
        action: 'submit',
        data: {
          existing_stack_id: existingStackId ?? undefined,
          preview_stack_size: pulls.length,
        },
      }),
    )

    // Backend expects position ASC (bottom-first); display order is top-first,
    // so we iterate in reverse. When appending, skip pulls already in a stack
    // -- the controller loads the existing membership from `stackId` itself.
    const pullRequestIds: number[] = []
    for (let i = pulls.length - 1; i >= 0; i--) {
      const pull = pulls[i]
      if (!pull) continue
      if (isExtendingExistingStack && pull.stackId != null) continue
      pullRequestIds.push(pull.id)
    }
    if (pullRequestIds.length === 0) return
    createStackOrAppend(
      {
        pullRequestIds,
        stackId: existingStackId,
      },
      {
        onSuccess: async response => {
          try {
            await queryClient.refetchQueries({queryKey: headerPageDataQueryKey}, {throwOnError: true})
            onStackCreated({stackNumber: response.stackNumber, appended: isExtendingExistingStack})
            onClose()
          } catch {
            setSubmitError('Something unexpected happened. Please refresh the page.')
            setIsSubmitting(false)
          }
        },
        onError: error => {
          setSubmitError(error.message ?? 'Failed to create stack.')
          setIsSubmitting(false)
        },
      },
    )
  }

  const footer = (
    <Dialog.Footer>
      <div className={styles.footerLayout}>
        {isModified ? (
          <Button
            variant="danger"
            onClick={handleReset}
            disabled={isSubmitting}
            title="Reset to the original proposed stack"
          >
            Reset stack
          </Button>
        ) : null}
        <div className={styles.footerActions}>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={isSubmitting}
            loadingAnnouncement={isExtendingExistingStack ? 'Adding to stack' : 'Creating stack'}
            onClick={handleSubmit}
            disabled={isPending || !hasPulls}
          >
            {createStackLabel}
          </Button>
        </div>
      </div>
    </Dialog.Footer>
  )

  return (
    <Dialog title="Preview stack" returnFocusRef={returnFocusRef} onClose={onClose} renderFooter={() => footer}>
      <PreviewStackDescription
        isExtendingExistingStack={isExtendingExistingStack}
        existingStackNumber={existingStackNumber}
        hasOtherNewlyAddedPulls={hasOtherNewlyAddedPulls}
      />
      {submitError ? (
        <Flash variant="danger" className="mb-2">
          {submitError}
        </Flash>
      ) : null}
      <PreviewStackList
        pulls={pulls}
        isPending={isPending}
        showFailure={showFailure}
        onRemoveTop={handleRemoveTop}
        disableRemove={isSubmitting}
        currentPullNumber={currentPullNumber}
        containerRef={listContainerRef}
      />
    </Dialog>
  )
}
